import type { AuthenticationErrorContext } from "gemi/client";
import { useSyncExternalStore } from "react";

// The session-expired prompt's state, fed by `onAuthenticationError` on `init()` (app/client.tsx).
// gemi calls the hook once per incident, and only after `/auth/me` refused the session too, so a
// call here means the session really ended while the page stayed open. It never redirects: the
// banner offers sign-in and the page keeps whatever the user had on it.
//
// One slot, not a queue: a second incident while the banner is up replaces the sign-in target with
// the newer one (the page now on screen) rather than stacking a second banner.
//
// Only while a banner is mounted. gemi confirms against `/auth/me` a second after the refusal, so a
// confirmation can land after the signed-in layout has gone (the tab already went to sign-in or
// sign-up). Kept then, it would greet the user again after they sign back in, with a sign-in target
// pointing at the auth page. An incident with no banner to show it is dropped instead.
//
// And only for the session that is current when it would show. A confirmation gemi started before
// the user signed in again can come back after the new layout mounted (a slow `/auth/me` answer
// outliving a quick PIN sign-in). A mounted banner alone does not tell the two sessions apart, so
// before showing, the session is asked once more, uncached, at display time: a signed-in answer
// drops the incident. So does a host change during that check (the layout left in the meantime).
// Anything but a signed-in answer (401, 5xx, no connection) shows the banner, since gemi already
// confirmed the refusal once. It costs one extra request per confirmed incident, never otherwise.
//
// Module state, read through `useSyncExternalStore` with a null server snapshot: the hook only ever
// fires in the browser, and the server render never shows the banner.

type SessionExpired = { redirect: () => void };

let current: SessionExpired | null = null;
let hosts = 0;
// Moves whenever the last banner unmounts: a check started under an older generation is stale.
let generation = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export async function reportSessionExpired(
  context: AuthenticationErrorContext,
): Promise<void> {
  if (hosts === 0) return;
  const startedIn = generation;
  if (await stillSignedIn()) return;
  if (hosts === 0 || startedIn !== generation) return;
  current = { redirect: context.redirect };
  emit();
}

async function stillSignedIn(): Promise<boolean> {
  try {
    const response = await fetch("/api/auth/me", {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    return response.ok;
  } catch {
    return false;
  }
}

export function dismissSessionExpired() {
  if (current === null) return;
  current = null;
  emit();
}

// Called by SessionExpiredBanner for as long as it is mounted. Unmounting the last one ends the
// incident.
export function hostSessionExpiredBanner() {
  hosts += 1;
  return () => {
    hosts -= 1;
    if (hosts === 0) {
      generation += 1;
      dismissSessionExpired();
    }
  };
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useSessionExpired(): SessionExpired | null {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => null,
  );
}
